import React, { useState, useEffect } from 'react';
import { FiDownload, FiX, FiRefreshCw } from 'react-icons/fi';
import '../styles/ServiceWorkerUpdateDialog.css';

const ServiceWorkerUpdateDialog = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Listen for service worker updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Track the registration so we can send it messages
      let registrationInstance = null;
      
      const checkForUpdates = async () => {
        try {
          registrationInstance = await navigator.serviceWorker.getRegistration();
          setRegistration(registrationInstance);
          
          if (registrationInstance) {
            // Check for updates immediately
            await registrationInstance.update();
            
            // Listen for the 'updatefound' event
            registrationInstance.addEventListener('updatefound', () => {
              const newWorker = registrationInstance.installing;
              
              newWorker.addEventListener('statechange', () => {
                // When the new service worker is installed, show update notification
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setUpdateAvailable(true);
                  setShowUpdate(true);
                }
              });
            });
          }
        } catch (error) {
          console.error('Error checking for updates:', error);
        }
      };

      // Check for updates on load
      checkForUpdates();
      
      // Check for updates every hour
      const updateInterval = setInterval(checkForUpdates, 60 * 60 * 1000);
      
      // Listen for messages from the service worker
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'NEW_VERSION_AVAILABLE') {
          setUpdateAvailable(true);
          setShowUpdate(true);
        }
      };
      
      navigator.serviceWorker.addEventListener('message', handleMessage);
      
      // Clean up event listeners and interval
      return () => {
        clearInterval(updateInterval);
        navigator.serviceWorker.removeEventListener('message', handleMessage);
        
        if (registrationInstance) {
          registrationInstance.removeEventListener('updatefound', checkForUpdates);
        }
      };
    }
  }, []);

  const handleUpdate = async () => {
    if (!registration || !registration.waiting) {
      // If there's no waiting worker, just reload
      window.location.reload();
      return;
    }

    try {
      setIsUpdating(true);
      
      // Tell the service worker to skip waiting and activate the new version
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Wait for the new service worker to take control
      await new Promise((resolve) => {
        navigator.serviceWorker.addEventListener('controllerchange', resolve, { once: true });
      });
      
      // Reload the page to use the new service worker
      window.location.reload();
    } catch (error) {
      console.error('Error updating service worker:', error);
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    setShowUpdate(false);
    // Don't show the update notification again for this version
    localStorage.setItem('lastDismissedVersion', process.env.REACT_APP_VERSION || '1.0.0');
  };

  if (!showUpdate || !updateAvailable) {
    return null;
  }

  return (
    <div className="update-dialog" role="alertdialog" aria-labelledby="update-dialog-title" aria-describedby="update-dialog-description">
      <div className="update-dialog-content">
        <div className="update-dialog-header">
          <h2 id="update-dialog-title" className="update-dialog-title">Update Available</h2>
          <button 
            className="update-dialog-close" 
            onClick={handleDismiss}
            aria-label="Dismiss update notification"
          >
            <FiX size={20} />
          </button>
        </div>
        
        <div className="update-dialog-body">
          <div className="update-icon">
            <FiDownload size={32} />
          </div>
          <p id="update-dialog-description" className="update-message">
            A new version of Corre is available. Update now to get the latest features and improvements.
          </p>
        </div>
        
        <div className="update-dialog-actions">
          <button 
            className="update-button" 
            onClick={handleUpdate}
            disabled={isUpdating}
            aria-busy={isUpdating}
          >
            {isUpdating ? (
              <>
                <FiRefreshCw className="spin" /> Updating...
              </>
            ) : (
              'Update Now'
            )}
          </button>
          
          <button 
            className="dismiss-button" 
            onClick={handleDismiss}
            disabled={isUpdating}
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceWorkerUpdateDialog;
